import React, { useState, useEffect } from 'react';
import {
  X,
  Calendar,
  Clock,
  MapPin,
  Video,
  Users,
  Search,
  Check,
  Loader2,
  AlertCircle,
} from 'lucide-react';
import { eventsApi, messagesApi, coursesApi } from '../../../lib/api';
import { useAuth } from '../../../app/providers/AuthProvider';

interface CreateEventModalProps {
  onClose: () => void;
  onCreated: () => void;
  defaultDate?: Date;
}

interface User {
  id: string;
  name: string;
  email: string;
  avatar: string | null;
}

interface Course {
  id: string;
  name: string;
}

type EventType = 'lesson' | 'class' | 'meeting' | 'event' | 'office_hours' | 'break' | 'holiday';

export function CreateEventModal({ onClose, onCreated, defaultDate }: CreateEventModalProps) {
  const { currentSchool, user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [eventType, setEventType] = useState<EventType>('meeting');
  const [startDate, setStartDate] = useState(
    defaultDate ? defaultDate.toISOString().split('T')[0] : new Date().toISOString().split('T')[0]
  );
  const [startTime, setStartTime] = useState('09:00');
  const [endDate, setEndDate] = useState(
    defaultDate ? defaultDate.toISOString().split('T')[0] : new Date().toISOString().split('T')[0]
  );
  const [endTime, setEndTime] = useState('10:00');
  const [allDay, setAllDay] = useState(false);
  const [location, setLocation] = useState('');
  const [createVideoMeeting, setCreateVideoMeeting] = useState(true);
  const [selectedCourseId, setSelectedCourseId] = useState<string | null>(null);

  // Invitation state
  const [showInvitePanel, setShowInvitePanel] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<User[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<User[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [guestEmail, setGuestEmail] = useState('');
  const [guestEmails, setGuestEmails] = useState<string[]>([]);

  // Courses
  const [courses, setCourses] = useState<Course[]>([]);

  // Load courses
  useEffect(() => {
    const loadCourses = async () => {
      try {
        const response = await coursesApi.list();
        setCourses(response.courses || []);
      } catch (err) {
        console.error('Failed to load courses:', err);
      }
    };
    loadCourses();
  }, []);

  // Search users when query changes
  useEffect(() => {
    if (!searchQuery || searchQuery.length < 2 || !currentSchool) {
      setSearchResults([]);
      return;
    }

    const searchUsers = async () => {
      setSearchLoading(true);
      try {
        const response = await messagesApi.searchUsers(searchQuery, currentSchool.id);
        // Filter out already selected users
        const filtered = (response.users || []).filter(
          (u) => !selectedUsers.find((s) => s.id === u.id)
        );
        setSearchResults(filtered);
      } catch (err) {
        console.error('Failed to search users:', err);
      } finally {
        setSearchLoading(false);
      }
    };

    const debounce = setTimeout(searchUsers, 300);
    return () => clearTimeout(debounce);
  }, [searchQuery, currentSchool, selectedUsers]);

  const handleSelectUser = (selectedUser: User) => {
    setSelectedUsers([...selectedUsers, selectedUser]);
    setSearchQuery('');
    setSearchResults([]);
  };

  const handleRemoveUser = (userId: string) => {
    setSelectedUsers(selectedUsers.filter((u) => u.id !== userId));
  };

  const handleAddGuestEmail = () => {
    if (guestEmail && guestEmail.includes('@') && !guestEmails.includes(guestEmail)) {
      setGuestEmails([...guestEmails, guestEmail]);
      setGuestEmail('');
    }
  };

  const handleRemoveGuestEmail = (email: string) => {
    setGuestEmails(guestEmails.filter((e) => e !== email));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentSchool) return;

    setLoading(true);
    setError(null);

    try {
      const startDateTime = allDay
        ? `${startDate}T00:00:00`
        : `${startDate}T${startTime}:00`;
      const endDateTime = allDay
        ? `${endDate}T23:59:59`
        : `${endDate}T${endTime}:00`;

      const eventData = {
        school_id: currentSchool.id,
        course_id: selectedCourseId || undefined,
        title,
        description: description || undefined,
        type: eventType,
        start_at: startDateTime,
        end_at: endDateTime,
        all_day: allDay,
        location: location || undefined,
        create_video_meeting: createVideoMeeting && ['lesson', 'class', 'meeting', 'event', 'office_hours'].includes(eventType),
      };

      await eventsApi.create(eventData);

      // TODO: Send invitations to selected users and guests
      // This would call a separate invitation API endpoint

      onCreated();
    } catch (err: any) {
      setError(err.message || 'Failed to create event');
    } finally {
      setLoading(false);
    }
  };

  const eventTypes: { value: EventType; label: string; description: string }[] = [
    { value: 'lesson', label: 'Lesson', description: 'Scheduled class with recording' },
    { value: 'meeting', label: 'Meeting', description: 'General meeting' },
    { value: 'event', label: 'Event', description: 'Special event or webinar' },
    { value: 'office_hours', label: 'Office Hours', description: 'Open session for questions' },
    { value: 'break', label: 'Break', description: 'Break or lunch time' },
    { value: 'holiday', label: 'Holiday', description: 'School holiday' },
  ];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Create Event</h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-6">
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg">
              <AlertCircle className="w-5 h-5" />
              {error}
            </div>
          )}

          {/* Event Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Event Type
            </label>
            <div className="grid grid-cols-3 gap-2">
              {eventTypes.map((type) => (
                <button
                  key={type.value}
                  type="button"
                  onClick={() => setEventType(type.value)}
                  className={`p-3 rounded-lg border text-left transition-colors ${
                    eventType === type.value
                      ? 'border-teal-500 bg-teal-50 text-teal-700'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="font-medium text-sm">{type.label}</div>
                  <div className="text-xs text-gray-500">{type.description}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Title *
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
              placeholder="Event title"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none"
              placeholder="Event description (optional)"
            />
          </div>

          {/* Course Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Course (optional)
            </label>
            <select
              value={selectedCourseId || ''}
              onChange={(e) => setSelectedCourseId(e.target.value || null)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
            >
              <option value="">No course</option>
              {courses.map((course) => (
                <option key={course.id} value={course.id}>
                  {course.name}
                </option>
              ))}
            </select>
          </div>

          {/* All Day Toggle */}
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="allDay"
              checked={allDay}
              onChange={(e) => setAllDay(e.target.checked)}
              className="w-4 h-4 text-teal-600 rounded focus:ring-teal-500"
            />
            <label htmlFor="allDay" className="text-sm text-gray-700">
              All day event
            </label>
          </div>

          {/* Date and Time */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Start
              </label>
              <div className="flex gap-2">
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  required
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
                {!allDay && (
                  <input
                    type="time"
                    value={startTime}
                    onChange={(e) => setStartTime(e.target.value)}
                    required
                    className="w-28 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                )}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                End
              </label>
              <div className="flex gap-2">
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  required
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
                {!allDay && (
                  <input
                    type="time"
                    value={endTime}
                    onChange={(e) => setEndTime(e.target.value)}
                    required
                    className="w-28 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                )}
              </div>
            </div>
          </div>

          {/* Location */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Location
            </label>
            <div className="relative">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                placeholder="Room or location"
              />
            </div>
          </div>

          {/* Video Meeting Toggle */}
          {['lesson', 'class', 'meeting', 'event', 'office_hours'].includes(eventType) && (
            <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
              <input
                type="checkbox"
                id="videoMeeting"
                checked={createVideoMeeting}
                onChange={(e) => setCreateVideoMeeting(e.target.checked)}
                className="w-4 h-4 text-teal-600 rounded focus:ring-teal-500"
              />
              <label htmlFor="videoMeeting" className="flex items-center gap-2 text-sm text-gray-700">
                <Video className="w-5 h-5 text-teal-600" />
                Create video meeting
              </label>
              {eventType === 'lesson' && createVideoMeeting && (
                <span className="ml-auto text-xs text-orange-600 bg-orange-100 px-2 py-1 rounded">
                  Auto-recording enabled
                </span>
              )}
            </div>
          )}

          {/* Invite Participants */}
          <div>
            <button
              type="button"
              onClick={() => setShowInvitePanel(!showInvitePanel)}
              className="flex items-center gap-2 text-sm text-teal-600 hover:text-teal-700"
            >
              <Users className="w-5 h-5" />
              {selectedUsers.length > 0 || guestEmails.length > 0
                ? `${selectedUsers.length + guestEmails.length} invited`
                : 'Invite participants'}
            </button>

            {showInvitePanel && (
              <div className="mt-3 p-4 bg-gray-50 rounded-lg space-y-4">
                {/* Search Users */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Search school members
                  </label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                      placeholder="Search by name or email"
                    />
                    {searchLoading && (
                      <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 animate-spin" />
                    )}
                  </div>

                  {/* Search Results */}
                  {searchResults.length > 0 && (
                    <div className="mt-2 max-h-40 overflow-y-auto bg-white border border-gray-200 rounded-lg">
                      {searchResults.map((searchUser) => (
                        <button
                          key={searchUser.id}
                          type="button"
                          onClick={() => handleSelectUser(searchUser)}
                          className="w-full flex items-center gap-3 p-3 hover:bg-gray-50 text-left"
                        >
                          <div className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center text-teal-700 font-medium">
                            {searchUser.name.charAt(0)}
                          </div>
                          <div>
                            <div className="text-sm font-medium text-gray-900">{searchUser.name}</div>
                            <div className="text-xs text-gray-500">{searchUser.email}</div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Selected Users */}
                {selectedUsers.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Invited members
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {selectedUsers.map((selectedUser) => (
                        <span
                          key={selectedUser.id}
                          className="inline-flex items-center gap-1 px-3 py-1 bg-teal-100 text-teal-700 rounded-full text-sm"
                        >
                          {selectedUser.name}
                          <button
                            type="button"
                            onClick={() => handleRemoveUser(selectedUser.id)}
                            className="p-0.5 hover:bg-teal-200 rounded-full"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Guest Emails */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Invite guests (external)
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="email"
                      value={guestEmail}
                      onChange={(e) => setGuestEmail(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddGuestEmail())}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                      placeholder="Enter guest email"
                    />
                    <button
                      type="button"
                      onClick={handleAddGuestEmail}
                      className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                    >
                      Add
                    </button>
                  </div>

                  {guestEmails.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {guestEmails.map((email) => (
                        <span
                          key={email}
                          className="inline-flex items-center gap-1 px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm"
                        >
                          {email}
                          <button
                            type="button"
                            onClick={() => handleRemoveGuestEmail(email)}
                            className="p-0.5 hover:bg-gray-200 rounded-full"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </form>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="px-6 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-100"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading || !title}
            className="flex items-center gap-2 px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            Create Event
          </button>
        </div>
      </div>
    </div>
  );
}
